import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { formatDateOnly, codeLabel, errorWords } from "@alm/domain";
import { EmptyState } from "@/components/app/lists/empty-state";
import { FillPill } from "@/components/app/patterns/fill-pill";
import { ListSkeleton } from "@/components/app/patterns/list-skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { XCircle } from "@/lib/icons";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import { invalidateKeys } from "@/lib/query/invalidate-keys";
import { CosignTable } from "./-components/cosign-table";

export const Route = createFileRoute("/_authenticated/_care/therapy-cosign-queue/")({
  component: CosignQueue,
});

const fail = (error: Error) => toast.error(errorWords(error));

// The notes, the fax picker that lists them, and the signer's activity feed.
const COSIGN_READERS = [orpc.care.therapy.key(), orpc.communications.fax.key()];

/** Notes an assistant has signed that are waiting on their supervisor. */
function CosignQueue() {
  const { facilityId } = useFacility();
  const queryClient = useQueryClient();
  const [signing, setSigning] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [assessment, setAssessment] = useState("");
  /* Bulk sign skips the supervision visit and reports it as needing the note: its assessment is
   * never invented. */
  const [chosen, setChosen] = useState<string[]>([]);
  const [signingAll, setSigningAll] = useState(false);
  /* What the bulk sign refused, and why, kept on the page rather than in a
     toast: it is a list of notes somebody now has to open one at a time. */
  const [refusals, setRefusals] = useState<{ noteId: string; label: string; error: string }[]>([]);

  const queue = useQuery(orpc.care.therapy.awaitingCosign.queryOptions({ input: { facilityId } }));

  const cosign = useMutation(
    orpc.care.therapy.cosign.mutationOptions({
      onSuccess: async () => {
        toast.success("Co-signed");
        setSigning(null);
        setPassword("");
        setAssessment("");
        await invalidateKeys(queryClient, COSIGN_READERS);
      },
      onError: fail,
    }),
  );

  const bulk = useMutation(
    orpc.care.therapy.cosignBulk.mutationOptions({
      onSuccess: async (result) => {
        const refused = result.results.filter((row) => !row.ok);
        const labels = new Map(
          (queue.data ?? []).map((note) => [
            note.id,
            `${note.residentName}. ${note.title ?? codeLabel(note.noteType)} (${formatDateOnly(note.noteDate)})`,
          ]),
        );
        setRefusals(
          refused.map((row) => ({
            noteId: row.noteId,
            label: labels.get(row.noteId) ?? row.noteId,
            error: row.error ?? "Could not be co-signed",
          })),
        );
        if (result.succeeded > 0) {
          toast.success(`Co-signed ${result.succeeded} note${result.succeeded === 1 ? "" : "s"}`);
        } else if (refused.length > 0) {
          toast.error("No notes were co-signed. See the details on the page.");
        }
        setSigningAll(false);
        setPassword("");
        setChosen([]);
        await invalidateKeys(queryClient, COSIGN_READERS);
      },
      onError: fail,
    }),
  );

  const toggle = (noteId: string) =>
    setChosen((current) =>
      current.includes(noteId) ? current.filter((id) => id !== noteId) : [...current, noteId],
    );

  const notes = queue.data ?? [];
  const routine = notes.filter((note) => !note.supervisionDue);
  const supervision = notes.filter((note) => note.supervisionDue);

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Awaiting my co-signature"
        count={queue.isSuccess ? notes.length : undefined}
        meta="Therapy notes signed by an assistant that you supervise. Review and apply your supervisory co-signature."
        actions={
          chosen.length > 0 ? (
            <Button onClick={() => setSigningAll(true)}>
              Co-sign {chosen.length} {chosen.length === 1 ? "note" : "notes"}
            </Button>
          ) : null
        }
      />

      {refusals.length > 0 ? (
        <Alert variant="destructive">
          <XCircle className="size-4" />
          <AlertDescription>
            <p className="font-medium">
              {refusals.length} note{refusals.length === 1 ? "" : "s"} could not be co-signed:
            </p>
            <ul className="mt-1 list-disc space-y-0.5 pl-5">
              {refusals.map((refusal) => (
                <li key={refusal.noteId}>
                  {refusal.label}: {refusal.error}
                </li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      ) : null}

      {queue.isPending || queue.isError || notes.length === 0 ? (
        <Card className="gap-0 py-0">
          {queue.isPending ? (
            <ListSkeleton />
          ) : queue.isError ? (
            <ReadFailed
              className="p-4"
              message="Could not load the notes awaiting your co-signature."
              error={queue.error}
              onRetry={() => void queue.refetch()}
              retrying={queue.isFetching}
            />
          ) : (
            <EmptyState
              title="Nothing waiting"
              description="No notes are awaiting your co-signature."
            />
          )}
        </Card>
      ) : (
        <>
          {routine.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle>Ready to co-sign</CardTitle>
                <CardDescription>
                  {routine.length} note{routine.length === 1 ? "" : "s"}, your password is all these
                  need
                </CardDescription>
              </CardHeader>
              <CardContent>
                <CosignTable
                  rows={routine}
                  chosen={chosen}
                  onChoose={setChosen}
                  onToggle={toggle}
                  onSign={setSigning}
                />
              </CardContent>
            </Card>
          ) : null}
          {supervision.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  Supervision visit
                  <FillPill tone="warn">{supervision.length}</FillPill>
                </CardTitle>
                <CardDescription>
                  These need your own supervisory assessment, so they are signed one at a time.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <CosignTable rows={supervision} onSign={setSigning} quiet />
              </CardContent>
            </Card>
          ) : null}
        </>
      )}

      <Dialog
        open={signingAll}
        onOpenChange={(open) => {
          if (!open) setPassword("");
          setSigningAll(open);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Co-sign {chosen.length} {chosen.length === 1 ? "note" : "notes"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-muted-foreground text-sm">
              Your password is checked once. A supervision visit needs its own assessment and is
              reported back rather than signed without one.
            </p>
            <div className="space-y-1">
              <Label htmlFor="bulk-password">Your password</Label>
              <Input
                id="bulk-password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setPassword("");
                setSigningAll(false);
              }}
            >
              Cancel
            </Button>
            <Button
              disabled={!password || bulk.isPending}
              onClick={() => bulk.mutate({ noteIds: chosen, password })}
            >
              Co-sign notes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={signing !== null} onOpenChange={(open) => !open && setSigning(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Co-sign this note</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="cosign-assessment">Supervisory assessment</Label>
              <Textarea
                id="cosign-assessment"
                value={assessment}
                onChange={(event) => setAssessment(event.target.value)}
              />
              <p className="text-muted-foreground text-sm">
                Required on the supervision visit, where you have to evidence that you took part.
              </p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="cosign-password">Your password</Label>
              <Input
                id="cosign-password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSigning(null)}>
              Cancel
            </Button>
            <Button
              disabled={!password || cosign.isPending}
              onClick={() =>
                signing &&
                cosign.mutate({
                  noteId: signing,
                  password,
                  supervisoryAssessment: assessment || null,
                })
              }
            >
              Co-sign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
